import i18n from 'i18next'
import { supabase } from '@/lib/supabase'
import { useAuthStore } from '@/stores/auth'
import {
  STORAGE_KEYS,
  SUPPORTED_LANGUAGES,
  type SupportedLanguage,
} from '@/lib/constants'

/**
 * The ONE place that decides which language a surface renders in (N-13).
 *
 * ## The precedence, in order
 *
 *   0. the address                     `?lang=nb|en`, read by i18next detection
 *                                      on load and never stored (the sitemap's
 *                                      hreflang links; see i18n/config.ts)
 *   1. the visitor's explicit choice   `profiles.language_preference` when
 *                                      signed in, else STORAGE_KEYS.LANGUAGE
 *   2. the event's declared language   `events.language`, via useEventLanguage
 *   3. the browser                     navigator, through i18next detection
 *   4. the product default             nb (`fallbackLng`)
 *
 * ## Why a choice needs its own storage key
 *
 * i18next-browser-languagedetector with `caches: ['localStorage']` writes the
 * DETECTED language to `i18nextLng` during init — read in i18next 25.x's own
 * source, `changeLanguage`'s `setLng` calls `cacheUserLanguage(l)` on the value
 * the detector just sniffed off `navigator`. So the moment an English browser
 * loaded any page once, `i18nextLng` said `en` and was indistinguishable from
 * somebody having picked English from the menu.
 *
 * That is the same conflation the migration removed from
 * `profiles.language_preference` (NOT NULL DEFAULT 'en' → nullable, NULL means
 * never asked), and it is fixed the same way rather than papered over with a
 * second marker key: i18next caches NOTHING (`caches: []`), and the key it
 * reads is written ONLY by `setLanguageChoice` below. Present means chosen.
 *
 * The legacy `i18nextLng` key is retired on boot by `retireLegacyLanguageCache`
 * — it holds sniffed values for everyone who has used the app, and leaving it
 * readable would have kept them on English forever after this change.
 */

export function isSupportedLanguage(v: unknown): v is SupportedLanguage {
  return typeof v === 'string' && (SUPPORTED_LANGUAGES as readonly string[]).includes(v)
}

/** The visitor's stored explicit choice, or null when they have never picked. */
export function readStoredLanguageChoice(): SupportedLanguage | null {
  try {
    const v = localStorage.getItem(STORAGE_KEYS.LANGUAGE)
    return isSupportedLanguage(v) ? v : null
  } catch {
    return null // private mode
  }
}

/**
 * Drop the pre-N-13 cache key. It was written by i18next's detector from
 * `navigator`, never by a person, so keeping it would let a browser's language
 * masquerade as a choice and outrank the event forever. Idempotent, and safe to
 * run on every boot: nothing writes that key any more.
 */
export function retireLegacyLanguageCache(): void {
  try {
    localStorage.removeItem(STORAGE_KEYS.LEGACY_LANGUAGE_CACHE)
  } catch {
    /* private mode */
  }
}

/**
 * A human picked a language. This is the ONLY writer of the stored choice and
 * the ONLY writer of `profiles.language_preference`.
 *
 * It used to be two writers plus a blanket `i18n.on('languageChanged')` listener
 * in LanguageProvider, and that listener is why this function has to exist: once
 * an EVENT can drive `changeLanguage`, a listener that persists every change
 * would quietly turn "this page is Norwegian" into "this person prefers
 * Norwegian" on their account, forever, without them touching anything.
 */
export async function setLanguageChoice(lng: SupportedLanguage): Promise<void> {
  try {
    localStorage.setItem(STORAGE_KEYS.LANGUAGE, lng)
  } catch {
    /* private mode — the choice still applies for this session */
  }
  await i18n.changeLanguage(lng)

  const profile = useAuthStore.getState().profile
  if (!profile?.id || profile.language_preference === lng) return

  const { error } = await supabase
    .from('profiles')
    .update({ language_preference: lng })
    .eq('id', profile.id)
  if (!error) await useAuthStore.getState().refreshProfile()
}
